/**
 * The colours a teacher can apply to their own text — one list, shared by the three editors that
 * offer it (rich text, the math editor, the reactions editor), each of which used to carry its own
 * copy of the same thirty entries.
 *
 * ── WHY TOKENS AND NOT HEXES ──────────────────────────────────────────────────────────────────
 * The palette is stored as `var(--ws-ink-<hue>-<tone>)` so the THEME can decide what a tone means.
 * Every tone takes a per-theme value, but for two different reasons:
 *
 *   `-light` / `-dark`  SWAP with each other in the dark theme. Two named swatches trade places.
 *   `-mid`              is CALIBRATED: the same colour, tuned to the ground it sits on.
 *
 * Both preserve the author's effective CHOICE rather than the literal colour. Measured across all
 * ten hues: a dark tone reads at 5.0-10.3:1 on white and its inverted partner at 8.7-12.4:1 on
 * navy, so readable stays readable; a light tone stays at 1.4-3.5:1 either way, so a deliberately
 * subtle choice stays subtle. Without the swap a teacher picks a readable colour in light mode and
 * hands a dark-mode student one they cannot read, with nothing in the editor to reveal it.
 *
 * ── WHY MID IS CALIBRATED AND NOT SWAPPED ─────────────────────────────────────────────────────
 * Four hues are intrinsically light. Orange, amber, green and teal cannot have one mid value that
 * both reads on white and stays luminous on navy: a single value dark enough for white (3.0:1) cost
 * amber 8.1:1 -> 5.8:1 on navy and visibly dulled it. So those four carry a slightly darker value in
 * the light theme and their original value in the dark one. Nothing gets worse on either ground.
 *
 * The other six mids are the same value in both, because they never needed the split. A mid swatch
 * therefore stays SOLID in the picker whatever its two values are: unlike the light/dark swap this
 * is not two swatches trading places, it is one colour holding its identity, and drawing L/D halves
 * on it would claim a change that is not happening.
 *
 * ── WHAT THIS IS NOT ──────────────────────────────────────────────────────────────────────────
 * It is NOT a contrast filter. None of these thirty clears WCAG AA on both surfaces, and that is
 * deliberate: a version that guaranteed AA was built, cost too much expressive range, and was
 * reverted. Coloured text here is EMPHASIS — a word or a phrase — where meaning never rests on the
 * colour. Poor contrast can also be the point, if the lesson is about colour design.
 *
 * What the palette DOES guarantee, since the mid calibration, is that nothing in it is UNREADABLE:
 * every one of the thirty clears 3:1 on the ground it renders on, except the light tones, which are
 * the deliberately subtle option and say so in their name. That is why the accessibility report no
 * longer inspects palette colours at all — a curated set answering for its own readability is the
 * fix; a warning about a colour we offered is a tax on using it.
 *
 * If you are tempted to prune this list on contrast grounds: that trade has been made once and
 * reversed. See `ink-colors.test.ts` for the numbers.
 *
 * ── AND WHY A HEX MAP ─────────────────────────────────────────────────────────────────────────
 * Two things cannot hold a `var()`. FORMULAS bake their colour into the LaTeX source, where a CSS
 * variable would not survive; and LEGACY CONTENT holds whichever hex was stored before this. Both
 * are remapped when RENDERED, which needs no migration pass and self-heals on display.
 */

export type InkTone = 'light' | 'mid' | 'dark';
export interface InkColor {
  labelKey: string;
  tone: InkTone;
  hue: string;
  /** What to store: a token, so the theme decides the tone. */
  value: string;
  /** What this swatch renders as in each theme. Drives the split preview in the picker, which is
   * how a teacher learns the light/dark pairing without being told. On a `mid` these may DIFFER
   * (see the calibration note above) and the swatch still draws solid — it paints from `value`, so
   * CSS resolves whichever of the two applies. Read them with `inkRendered`, never directly. */
  onLight: string;
  onDark: string;
}

/** Ten hues, three tones each, in picker order. */
export const INK_COLORS: InkColor[] = [
  { labelKey: 'blocks.colors.lightGray', tone: 'light', hue: 'gray', value: 'var(--ws-ink-gray-light)', onLight: '#D1D5DB', onDark: '#374151' },
  { labelKey: 'blocks.colors.gray', tone: 'mid', hue: 'gray', value: 'var(--ws-ink-gray-mid)', onLight: '#6B7280', onDark: '#6B7280' },
  { labelKey: 'blocks.colors.darkGray', tone: 'dark', hue: 'gray', value: 'var(--ws-ink-gray-dark)', onLight: '#374151', onDark: '#D1D5DB' },
  { labelKey: 'blocks.colors.lightRed', tone: 'light', hue: 'red', value: 'var(--ws-ink-red-light)', onLight: '#FCA5A5', onDark: '#B91C1C' },
  { labelKey: 'blocks.colors.red', tone: 'mid', hue: 'red', value: 'var(--ws-ink-red-mid)', onLight: '#EF4444', onDark: '#EF4444' },
  { labelKey: 'blocks.colors.darkRed', tone: 'dark', hue: 'red', value: 'var(--ws-ink-red-dark)', onLight: '#B91C1C', onDark: '#FCA5A5' },
  { labelKey: 'blocks.colors.lightOrange', tone: 'light', hue: 'orange', value: 'var(--ws-ink-orange-light)', onLight: '#FDBA74', onDark: '#C2410C' },
  { labelKey: 'blocks.colors.orange', tone: 'mid', hue: 'orange', value: 'var(--ws-ink-orange-mid)', onLight: '#F76906', onDark: '#F97316' },
  { labelKey: 'blocks.colors.darkOrange', tone: 'dark', hue: 'orange', value: 'var(--ws-ink-orange-dark)', onLight: '#C2410C', onDark: '#FDBA74' },
  { labelKey: 'blocks.colors.lightAmber', tone: 'light', hue: 'amber', value: 'var(--ws-ink-amber-light)', onLight: '#FCD34D', onDark: '#B45309' },
  { labelKey: 'blocks.colors.amber', tone: 'mid', hue: 'amber', value: 'var(--ws-ink-amber-mid)', onLight: '#CE8508', onDark: '#F59E0B' },
  { labelKey: 'blocks.colors.darkAmber', tone: 'dark', hue: 'amber', value: 'var(--ws-ink-amber-dark)', onLight: '#B45309', onDark: '#FCD34D' },
  { labelKey: 'blocks.colors.lightGreen', tone: 'light', hue: 'green', value: 'var(--ws-ink-green-light)', onLight: '#86EFAC', onDark: '#15803D' },
  { labelKey: 'blocks.colors.green', tone: 'mid', hue: 'green', value: 'var(--ws-ink-green-mid)', onLight: '#1DAB52', onDark: '#22C55E' },
  { labelKey: 'blocks.colors.darkGreen', tone: 'dark', hue: 'green', value: 'var(--ws-ink-green-dark)', onLight: '#15803D', onDark: '#86EFAC' },
  { labelKey: 'blocks.colors.lightTeal', tone: 'light', hue: 'teal', value: 'var(--ws-ink-teal-light)', onLight: '#5EEAD4', onDark: '#0F766E' },
  { labelKey: 'blocks.colors.teal', tone: 'mid', hue: 'teal', value: 'var(--ws-ink-teal-mid)', onLight: '#12A796', onDark: '#14B8A6' },
  { labelKey: 'blocks.colors.darkTeal', tone: 'dark', hue: 'teal', value: 'var(--ws-ink-teal-dark)', onLight: '#0F766E', onDark: '#5EEAD4' },
  { labelKey: 'blocks.colors.lightBlue', tone: 'light', hue: 'blue', value: 'var(--ws-ink-blue-light)', onLight: '#93C5FD', onDark: '#1D4ED8' },
  { labelKey: 'blocks.colors.blue', tone: 'mid', hue: 'blue', value: 'var(--ws-ink-blue-mid)', onLight: '#3B82F6', onDark: '#3B82F6' },
  { labelKey: 'blocks.colors.darkBlue', tone: 'dark', hue: 'blue', value: 'var(--ws-ink-blue-dark)', onLight: '#1D4ED8', onDark: '#93C5FD' },
  { labelKey: 'blocks.colors.lightIndigo', tone: 'light', hue: 'indigo', value: 'var(--ws-ink-indigo-light)', onLight: '#A5B4FC', onDark: '#4338CA' },
  { labelKey: 'blocks.colors.indigo', tone: 'mid', hue: 'indigo', value: 'var(--ws-ink-indigo-mid)', onLight: '#6366F1', onDark: '#6366F1' },
  { labelKey: 'blocks.colors.darkIndigo', tone: 'dark', hue: 'indigo', value: 'var(--ws-ink-indigo-dark)', onLight: '#4338CA', onDark: '#A5B4FC' },
  { labelKey: 'blocks.colors.lightPurple', tone: 'light', hue: 'purple', value: 'var(--ws-ink-purple-light)', onLight: '#D8B4FE', onDark: '#7E22CE' },
  { labelKey: 'blocks.colors.purple', tone: 'mid', hue: 'purple', value: 'var(--ws-ink-purple-mid)', onLight: '#A855F4', onDark: '#A855F4' },
  { labelKey: 'blocks.colors.darkPurple', tone: 'dark', hue: 'purple', value: 'var(--ws-ink-purple-dark)', onLight: '#7E22CE', onDark: '#D8B4FE' },
  { labelKey: 'blocks.colors.lightPink', tone: 'light', hue: 'pink', value: 'var(--ws-ink-pink-light)', onLight: '#F9A8D4', onDark: '#9D174D' },
  { labelKey: 'blocks.colors.pink', tone: 'mid', hue: 'pink', value: 'var(--ws-ink-pink-mid)', onLight: '#EC4899', onDark: '#EC4899' },
  { labelKey: 'blocks.colors.darkPink', tone: 'dark', hue: 'pink', value: 'var(--ws-ink-pink-dark)', onLight: '#9D174D', onDark: '#F9A8D4' },
];

/** Every hex the palette has ever written, to the token that now carries it. */
const HEX_TO_TOKEN: Record<string, string> = {
  '#d1d5db': 'var(--ws-ink-gray-light)',
  '#6b7280': 'var(--ws-ink-gray-mid)',
  '#374151': 'var(--ws-ink-gray-dark)',
  '#fca5a5': 'var(--ws-ink-red-light)',
  '#ef4444': 'var(--ws-ink-red-mid)',
  '#b91c1c': 'var(--ws-ink-red-dark)',
  '#fdba74': 'var(--ws-ink-orange-light)',
  '#f97316': 'var(--ws-ink-orange-mid)',
  '#f76906': 'var(--ws-ink-orange-mid)',
  '#c2410c': 'var(--ws-ink-orange-dark)',
  '#fcd34d': 'var(--ws-ink-amber-light)',
  '#f59e0b': 'var(--ws-ink-amber-mid)',
  '#ce8508': 'var(--ws-ink-amber-mid)',
  '#b45309': 'var(--ws-ink-amber-dark)',
  '#86efac': 'var(--ws-ink-green-light)',
  '#22c55e': 'var(--ws-ink-green-mid)',
  '#1dab52': 'var(--ws-ink-green-mid)',
  '#15803d': 'var(--ws-ink-green-dark)',
  '#5eead4': 'var(--ws-ink-teal-light)',
  '#14b8a6': 'var(--ws-ink-teal-mid)',
  '#12a796': 'var(--ws-ink-teal-mid)',
  '#0f766e': 'var(--ws-ink-teal-dark)',
  '#93c5fd': 'var(--ws-ink-blue-light)',
  '#3b82f6': 'var(--ws-ink-blue-mid)',
  '#1d4ed8': 'var(--ws-ink-blue-dark)',
  '#a5b4fc': 'var(--ws-ink-indigo-light)',
  '#6366f1': 'var(--ws-ink-indigo-mid)',
  '#4338ca': 'var(--ws-ink-indigo-dark)',
  '#d8b4fe': 'var(--ws-ink-purple-light)',
  '#a855f4': 'var(--ws-ink-purple-mid)',
  '#7e22ce': 'var(--ws-ink-purple-dark)',
  '#f9a8d4': 'var(--ws-ink-pink-light)',
  '#ec4899': 'var(--ws-ink-pink-mid)',
  '#9d174d': 'var(--ws-ink-pink-dark)',
};

/** `rgb(1, 2, 3)` -> `#010203`. Anything else comes back unchanged. */
function rgbToHex(value: string): string {
  const m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(value);
  if (!m) return value;
  return '#' + [m[1], m[2], m[3]].map(n => Number(n).toString(16).padStart(2, '0')).join('');
}

/**
 * The theme token that should carry a stored colour, or null if none should.
 *
 * Three rules, most specific first: an exact palette hex, a pasted near-black that is really the
 * default text colour, and a pasted colour imperceptibly close to a palette swatch. Anything else
 * is a colour someone genuinely chose and comes back null, staying exactly as it was written.
 *
 * Accepts `rgb()` as well as hex, which is not a nicety: TipTap round-trips inline styles through
 * the DOM, and the DOM serialises a colour as `rgb(209, 213, 219)`. So EVERY colour this palette
 * has ever stored is in that form, not the `#D1D5DB` the picker offered. A hex-only lookup matched
 * nothing at all — which is exactly how the theme swap came to be shipped doing nothing.
 */
export function inkTokenForColor(value: string): string | null {
  const v = value.trim().toLowerCase();
  if (v.startsWith('var(--ws-ink-')) return v;
  const hex = rgbToHex(v);
  // A palette hex wins outright, before either of the fuzzy rules below. This is a fast path and a
  // statement of intent rather than a behaviour: no palette value sits inside the default-text band,
  // and no two are within the snap threshold of each other, so the near-match below would resolve an
  // exact hex to the same token anyway. Both of those are asserted in `ink-colors.test.ts`; removing
  // this line breaks no test, by construction. Keep it — it is what makes the two properties
  // sufficient rather than load-bearing.
  const exact = HEX_TO_TOKEN[hex];
  if (exact) return exact;
  if (isEffectivelyDefaultText(hex)) return DEFAULT_TEXT_TOKEN;
  return nearPaletteToken(hex);
}

/** What every block already uses for ordinary text. It inverts with the theme on its own. */
const DEFAULT_TEXT_TOKEN = 'var(--ws-text)';
/** Dark enough to read as black. `#333` sits at 21 and stays out; our own `--ws-text` is 8. */
export const DEFAULT_TEXT_MAX_L = 18;
/** Neutral enough to read as black. Dark navy is 80 and very dark red 31; `--ws-text` is 14.5. */
export const DEFAULT_TEXT_MAX_CHROMA = 16;

/**
 * Is this pasted colour just "black"?
 *
 * Black is not a colour CHOICE, it is the absence of one. Every word processor writes body text as
 * `#000000`, and nobody picked it over `#0D0D0D` for effect — so pinning it literally serves no
 * intent while guaranteeing the worst outcome we have: black text on the dark card, at 1.1:1,
 * invisible to every dark-mode student. Mapping it to the token the rest of the task already uses
 * is what the author meant, and it inverts for free.
 *
 * Two bounds, both needed. LIGHTNESS alone would catch dark navy and very dark red, which ARE
 * choices; CHROMA alone would catch every grey including the palette's own. Together they enclose
 * the near-blacks and nothing else.
 *
 * Unlike the delta-E snap this can shift the rendered value slightly — pure black becomes the
 * theme's near-black, a gap of about 8 in Lab that is invisible in running text and, at the dark
 * end, smaller than the number suggests. That is the one visible-change licence taken here, and it
 * is taken because the alternative is a colour half the readers cannot see at all.
 *
 * NOT applied at the white end, deliberately. Pasted near-white is correct in dark mode and
 * invisible in light, so the equivalent rewrite would flip it to black in the theme it was authored
 * in — a drastic change — and the band would collide with the palette's own light greys, which sit
 * at L 85 to 92. That case stays with the accessibility report, where it can be seen and judged.
 */
function isEffectivelyDefaultText(hex: string): boolean {
  const lab = labOf(hex);
  if (!lab) return false;
  const [L, a, b] = lab;
  return L <= DEFAULT_TEXT_MAX_L && Math.hypot(a, b) <= DEFAULT_TEXT_MAX_CHROMA;
}

/** CIE76 in Lab. Good enough to answer "would anyone see the difference", which is all this asks. */
function labOf(hex: string): [number, number, number] | null {
  if (!/^#[0-9a-f]{6}$/.test(hex)) return null;
  const [r, g, b] = [1, 3, 5]
    .map(i => parseInt(hex.slice(i, i + 2), 16) / 255)
    .map(c => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)) as [number, number, number];
  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const X = f((0.4124 * r + 0.3576 * g + 0.1805 * b) / 0.95047);
  const Y = f(0.2126 * r + 0.7152 * g + 0.0722 * b);
  const Z = f((0.0193 * r + 0.1192 * g + 0.9505 * b) / 1.08883);
  return [116 * Y - 16, 500 * (X - Y), 200 * (Y - Z)];
}

/**
 * The just-noticeable difference for two colours seen side by side. Apart, as words in a paragraph
 * are, a gap this small is invisible; the standard figure is ~2.3 and this stays under it.
 */
export const INK_SNAP_DELTA_E = 2.3;

/**
 * A palette token for a colour that is imperceptibly close to one of ours.
 *
 * The near-silent failure this closes: paste `#B91C1D` from a document and you get a red nobody can
 * distinguish from Dark Red, except it is a fixed value, so it alone refuses to swap when a student
 * switches theme. The page looks right in the theme it was authored in and quietly wrong in the
 * other, and no warning is worth showing for a difference no one can see.
 *
 * Only fires under `INK_SNAP_DELTA_E`, so nothing visible is ever changed — a colour a teacher
 * actually chose for its own sake is far further out than this and stays exactly as pasted. And it
 * happens at RENDER, through `remapInkColors`, so stored content is untouched and the behaviour is
 * reversible by changing this constant.
 */
function nearPaletteToken(hex: string): string | null {
  const target = labOf(hex);
  if (!target) return null;
  let best: string | null = null;
  let bestD = INK_SNAP_DELTA_E;
  for (const [candidate, token] of Object.entries(HEX_TO_TOKEN)) {
    const lab = labOf(candidate);
    if (!lab) continue;
    const d = Math.hypot(target[0] - lab[0], target[1] - lab[1], target[2] - lab[2]);
    if (d < bestD) { bestD = d; best = token; }
  }
  return best;
}

/**
 * Rewrite palette colours in a fragment of HTML into their theme token.
 *
 * `(?<![-\w])` so this matches the `color` PROPERTY and not `background-color` or `border-color`,
 * which end in the same six letters — a background is a design decision that was never the
 * teacher's, so rewriting one would change something nobody asked to change.
 *
 * Unrecognised colours are left exactly as they are: pasted content keeps what it had, and stays
 * the accessibility report's job.
 */
export function remapInkColors(html: string): string {
  if (!html) return html;
  return html.replace(/(?<![-\w])(color\s*:\s*)(#[0-9a-fA-F]{6}|rgba?\([^)]*\))/g,
    (whole, prop: string, colour: string) => {
      const token = inkTokenForColor(colour);
      return token ? `${prop}${token}` : whole;
    });
}

/** What a tone renders as in a given theme. The swap lives here rather than at every call site. */
export const inkRendered = (c: InkColor, theme: InkTheme) => (theme === 'dark' ? c.onDark : c.onLight);

export type InkTheme = 'light' | 'dark';

/**
 * Which swatch produced this rendered colour — and it needs the THEME to answer.
 *
 * `inkTokenForColor` cannot do this job. It maps a hex to the token that *defines* it, but the swap
 * means one hex has two meanings: `#15803D` is Dark Green rendered in light mode AND Light Green
 * rendered in dark mode. Asking "which swatch did the teacher click" without saying which theme you
 * are looking at is unanswerable, and answering it wrong names the opposite end of the ramp — so the
 * report would tell a teacher to fix the one colour that was already fine.
 *
 * Within a single theme the thirty rendered values are distinct, so this is exact.
 */
export function inkColorForRendered(value: string, theme: InkTheme): InkColor | null {
  const hex = rgbToHex(value.trim()).toLowerCase();
  return INK_COLORS.find(c => inkRendered(c, theme).toLowerCase() === hex) ?? null;
}

/**
 * The same thirty as literals, in the same order — for the formula editors.
 *
 * MathLive's `applyStyle` bakes the colour into the LaTeX source, where a CSS `var()` would not
 * survive. So a formula stores a literal and `remapInkColors` swaps it for the token when the
 * formula is RENDERED, which is also what upgrades every formula authored before this.
 */
export const INK_LITERALS: string[] = [
  '#D1D5DB',
  '#6B7280',
  '#374151',
  '#FCA5A5',
  '#EF4444',
  '#B91C1C',
  '#FDBA74',
  '#F76906',
  '#C2410C',
  '#FCD34D',
  '#CE8508',
  '#B45309',
  '#86EFAC',
  '#1DAB52',
  '#15803D',
  '#5EEAD4',
  '#12A796',
  '#0F766E',
  '#93C5FD',
  '#3B82F6',
  '#1D4ED8',
  '#A5B4FC',
  '#6366F1',
  '#4338CA',
  '#D8B4FE',
  '#A855F4',
  '#7E22CE',
  '#F9A8D4',
  '#EC4899',
  '#9D174D',
];
